import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './IvsEvs.css';

function ChampionsGuide() {
  return (
    <>
      <section className="ivs-evs__section">
        <h2>No IVs in Pokémon Champions</h2>
        <p>
          Individual Values do not exist in Pokémon Champions. Every Pokémon — whether recruited
          in-game or imported from HOME — is calculated as if it has perfect 31 IVs in all stats.
          There is no variation, no Hyper Training, and no checking IVs.
        </p>
        <div className="ivs-evs__info-box">
          <h3>What this means for you:</h3>
          <ul>
            <li>All Pokémon have maximum stat potential from the start</li>
            <li>No need to breed, soft-reset, or use Bottle Caps</li>
            <li>HOME imports arrive with perfect IVs automatically</li>
            <li>Two Pokémon of the same species with the same Stat Points will have identical stats</li>
          </ul>
        </div>
      </section>

      <section className="ivs-evs__section">
        <h2>Stat Points (EV Equivalent)</h2>
        <p>
          Effort Values are replaced by <strong>Stat Points</strong>. Instead of grinding battles or
          using vitamins, you spend VP directly to allocate Stat Points through the training menu.
        </p>
        <div className="ivs-evs__info-box">
          <h3>Key Points about Stat Points:</h3>
          <ul>
            <li>Maximum of <strong>32 Stat Points per stat</strong> (equivalent to 252 EVs in S&amp;V)</li>
            <li>Each Stat Point costs <strong>5 VP</strong></li>
            <li>Maxing one stat from scratch costs 160 VP</li>
            <li>Stat Points can be freely reallocated — there is no penalty for changing your spread</li>
            <li><strong>Training Tickets</strong> let you apply Stat Points without spending VP at all</li>
          </ul>
        </div>

        <div className="ivs-evs__items-section">
          <h3>Stat Point Cost Reference</h3>
          <div className="ivs-evs__cost-table">
            <div className="ivs-evs__cost-row ivs-evs__cost-row--header">
              <span>Action</span>
              <span>VP Cost</span>
            </div>
            <div className="ivs-evs__cost-row">
              <span>1 Stat Point</span>
              <span>5 VP</span>
            </div>
            <div className="ivs-evs__cost-row">
              <span>Max one stat (32 points)</span>
              <span>160 VP</span>
            </div>
            <div className="ivs-evs__cost-row">
              <span>Standard 2-stat spread (32 / 32)</span>
              <span>320 VP</span>
            </div>
            <div className="ivs-evs__cost-row">
              <span>With Training Ticket</span>
              <span className="ivs-evs__cost-free">0 VP</span>
            </div>
          </div>
          <div className="ivs-evs__items-note">
            <p>
              Pokémon imported from HOME with EVs already trained convert automatically:
              the first 4 EVs in a stat = 1 Stat Point; each 8 EVs after that = 1 additional Stat Point.
              A fully EV-trained stat (252 EVs) arrives with all 32 Stat Points already allocated.
            </p>
          </div>
        </div>
      </section>

      <section className="ivs-evs__section">
        <h2>Other Training Costs</h2>
        <p>These are the VP costs for other customizations in the training menu:</p>
        <div className="ivs-evs__cost-table">
          <div className="ivs-evs__cost-row ivs-evs__cost-row--header">
            <span>Customization</span>
            <span>VP Cost</span>
          </div>
          <div className="ivs-evs__cost-row">
            <span>Change a move</span>
            <span>250 VP</span>
          </div>
          <div className="ivs-evs__cost-row">
            <span>Change Stat Alignment (Nature)</span>
            <span>500 VP</span>
          </div>
          <div className="ivs-evs__cost-row">
            <span>Change Ability</span>
            <span>500 VP</span>
          </div>
          <div className="ivs-evs__cost-row">
            <span>Any of the above with Training Ticket</span>
            <span className="ivs-evs__cost-free">0 VP</span>
          </div>
        </div>
      </section>

      <section className="ivs-evs__section">
        <h2>Competitive Importance</h2>
        <p>
          The Champions system is designed to lower the barrier to competitive play while keeping
          meaningful strategic depth. Key takeaways:
        </p>
        <ul className="ivs-evs__benefits">
          <li>No RNG — every Pokémon can be made optimal with enough VP</li>
          <li>Speed tiers are still critical; the same 32-point cap applies to Speed</li>
          <li>Spreads like 32 HP / 32 Def / 0 elsewhere are still viable defensive investments</li>
          <li>HOME imports from S&V with 252 EVs arrive fully trained in Champions</li>
        </ul>
      </section>
    </>
  );
}

function ScarletVioletGuide() {
  return (
    <>
      <section className="ivs-evs__section">
        <h2>Individual Values (IVs)</h2>
        <p>
          Individual Values (IVs) are hidden values that determine a Pokémon's base stats.
          Each stat (HP, Attack, Defense, Special Attack, Special Defense, and Speed) has an IV
          ranging from 0 to 31, with 31 being the best.
        </p>
        <div className="ivs-evs__info-box">
          <h3>Key Points about IVs:</h3>
          <ul>
            <li>IVs are determined when a Pokémon is generated (caught, hatched, or received)</li>
            <li>Each stat can have an IV between 0–31</li>
            <li>IVs cannot be changed through normal gameplay (except with Hyper Training)</li>
            <li>Higher IVs result in better stats</li>
          </ul>
        </div>
        <div className="ivs-evs__items-section">
          <h3>How to Maximize IVs</h3>
          <p>You can use the following items to improve your Pokémon's IVs through Hyper Training:</p>
          <div className="ivs-evs__items-grid">
            <Link to="/items/Bottle%20Cap" className="ivs-evs__item-card">
              <h4>Bottle Cap</h4>
              <p>Maximizes one IV stat to 31 through Hyper Training</p>
            </Link>
            <Link to="/items/Gold%20Bottle%20Cap" className="ivs-evs__item-card">
              <h4>Gold Bottle Cap</h4>
              <p>Maximizes all IV stats to 31 through Hyper Training</p>
            </Link>
          </div>
        </div>
      </section>

      <section className="ivs-evs__section">
        <h2>Effort Values (EVs)</h2>
        <p>
          Effort Values (EVs) are points that increase a Pokémon's stats based on the Pokémon it defeats.
          Each Pokémon can have a maximum of 510 total EVs, with no more than 252 EVs in any single stat.
        </p>
        <div className="ivs-evs__info-box">
          <h3>Key Points about EVs:</h3>
          <ul>
            <li>EVs are gained through battling or using items</li>
            <li>Maximum 252 EVs per stat</li>
            <li>Total maximum of 510 EVs across all stats</li>
            <li>Every 4 EVs equals +1 to the corresponding stat at level 100</li>
          </ul>
        </div>
        <div className="ivs-evs__items-section">
          <h3>Vitamins for EV Training</h3>
          <p>You can use these vitamins to quickly increase specific EVs:</p>
          <div className="ivs-evs__items-grid">
            <Link to="/items/HP%20Up" className="ivs-evs__item-card">
              <h4>HP Up</h4>
              <p>Increases HP EVs</p>
            </Link>
            <Link to="/items/Protein" className="ivs-evs__item-card">
              <h4>Protein</h4>
              <p>Increases Attack EVs</p>
            </Link>
            <Link to="/items/Iron" className="ivs-evs__item-card">
              <h4>Iron</h4>
              <p>Increases Defense EVs</p>
            </Link>
            <Link to="/items/Calcium" className="ivs-evs__item-card">
              <h4>Calcium</h4>
              <p>Increases Special Attack EVs</p>
            </Link>
            <Link to="/items/Zinc" className="ivs-evs__item-card">
              <h4>Zinc</h4>
              <p>Increases Special Defense EVs</p>
            </Link>
            <Link to="/items/Carbos" className="ivs-evs__item-card">
              <h4>Carbos</h4>
              <p>Increases Speed EVs</p>
            </Link>
          </div>
          <div className="ivs-evs__items-note">
            <p>Each vitamin increases the corresponding EV by 10 points. You can use up to 26 of the same vitamin on a single Pokémon.</p>
          </div>
        </div>
      </section>

      <section className="ivs-evs__section">
        <h2>Competitive Importance</h2>
        <p>
          Understanding IVs and EVs is crucial for competitive Pokémon battling. They allow you to:
        </p>
        <ul className="ivs-evs__benefits">
          <li>Maximize your Pokémon's potential in specific stats</li>
          <li>Create specialized builds for different strategies</li>
          <li>Optimize speed tiers and survivability</li>
          <li>Gain an edge in competitive battles</li>
        </ul>
      </section>
    </>
  );
}

const IvsEvs = () => {
  const [activeGame, setActiveGame] = useState('champions');

  return (
    <div className="ivs-evs">
      <div className="ivs-evs__header">
        <h1>IVs, EVs &amp; Stat Points</h1>
      </div>

      <div className="ivs-evs__tabs">
        <button
          className={`ivs-evs__tab${activeGame === 'champions' ? ' ivs-evs__tab--active' : ''}`}
          onClick={() => setActiveGame('champions')}
        >
          Pokémon Champions
          <span className="ivs-evs__tab-badge ivs-evs__tab-badge--current">Current</span>
        </button>
        <button
          className={`ivs-evs__tab${activeGame === 'sv' ? ' ivs-evs__tab--active' : ''}`}
          onClick={() => setActiveGame('sv')}
        >
          Scarlet &amp; Violet
          <span className="ivs-evs__tab-badge ivs-evs__tab-badge--legacy">Legacy</span>
        </button>
      </div>

      <div className="ivs-evs__content">
        {activeGame === 'champions' ? <ChampionsGuide /> : <ScarletVioletGuide />}
      </div>
    </div>
  );
};

export default IvsEvs;
